import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal } from 'antd'
import cn from 'classnames'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import classes from '../ActivateNotificationIntegration/index.module.css'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { useAddAlert } from '@/hooks/useAddAlert'
import { integrationRecipientApi } from '@/api/integrations'
import { isError } from '@/helpers/checkQuery'
import { selectProfile } from '@store/profile.reducer'
import {
  dispatchNotificationIntegrationRefetch,
  NOTIFICATION_INTEGRATION_UI,
  type NotificationIntegrationProvider,
} from '@/helpers/notificationIntegration'

export interface DeactivateNotificationIntegrationProps {
  provider: NotificationIntegrationProvider
  open?: boolean
  setOpen?: (open: boolean) => void
  integrationId?: number | null
  displayHandle?: string | null
}

const DeactivateNotificationIntegration: FC<DeactivateNotificationIntegrationProps> = ({
  provider,
  open = true,
  setOpen = () => {},
  integrationId = null,
  displayHandle = null,
}) => {
  const addAlert = useAddAlert()
  const ui = NOTIFICATION_INTEGRATION_UI[provider]
  const profile = useSelector(selectProfile)
  const userId = profile?.id

  const [isActive, setIsActive] = useState(open)
  const [submitting, setSubmitting] = useState(false)
  const [errorText, setErrorText] = useState<string | null>(null)

  useEffect(() => {
    setIsActive(open)
  }, [open])

  useEffect(() => {
    setOpen(isActive)
  }, [isActive, setOpen])

  const handleDetach = async () => {
    if (!integrationId || !userId) return

    setSubmitting(true)
    setErrorText(null)

    try {
      const res = await integrationRecipientApi.detachUser(integrationId, {
        user_id: Number(userId),
      })

      if (isError(res)) {
        setErrorText(ui.detachErrorText)
        return
      }

      addAlert({
        title: 'Успешно',
        text: ui.successDetachText,
        type: 'success',
      })

      setIsActive(false)
      dispatchNotificationIntegrationRefetch(provider)
    } catch {
      setErrorText(ui.detachErrorText)
    } finally {
      setSubmitting(false)
    }
  }

  const formattedHandle = ui.formatHandle(displayHandle)

  return (
    <Modal
      open={isActive}
      closable={false}
      footer={null}
      title={
        <div className={mclasses.modalHeader}>
          <p className={mclasses.modalHeaderTitle}>{ui.detachTitle}</p>
          <div className={mclasses.closeIcon} onClick={() => setIsActive(false)}>
            <IcoRemove />
          </div>
        </div>
      }
      centered
      maskClosable
      className={cn(amclasses.modal)}
    >
      <div className={classes.description}>
        <p>
          {ui.detachConfirmPrefix}
          {formattedHandle ? (
            <>
              {' '}
              (<strong>{formattedHandle}</strong>)
            </>
          ) : null}{' '}
          от вашего аккаунта?
        </p>
        <p>{ui.detachAfterText}</p>
      </div>

      {errorText && <div className={classes.errorText}>{errorText}</div>}

      <Button
        isLoading={submitting}
        onClick={handleDetach}
        className={classes.submitButton}
        disabled={!integrationId || !userId || submitting}
      >
        Отвязать
      </Button>
    </Modal>
  )
}

export default DeactivateNotificationIntegration
